import _ from 'underscore';

import { CompositeSessionInfoSelectors, SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '~selectors/SessionInfoSelectors';
import { ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { ISubscriptionInfo } from '~src/subscription/models/ISubscriptionInfo';

import { AppState } from '../../../../app/state/AppState';
import { ExtractEncryptionState, IDataSourceDetailInfo, IDataSourceInfo } from '../../../../datasource/models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../../../datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '../../../../datasource/resources/Datasources';
import { IDataAlertInfo } from '../../../../dda/models/IDataAlertInfo';
import { FlowBaseInfo } from '../../../../flow/models/FlowInfo';
import { ScheduleActions } from '../../../../schedule/types/ISchedule';
import { IScheduleInfo } from '../../../../schedule/types/IScheduleInfo';
import { ISiteSettingsForServerAdmin } from '../../../../settings/types/ISite';
import { ISharedWithMeContentInfo } from '../../../../sharing/models/ISharedWithMeContentInfo';
import { ExtractEncryptionMode, SiteAvailabilityState } from '../../../../sites/SitePropertyTypes';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { IGroupInfo } from '../../../../ts/models/IGroupInfo';
import { IExplorableItemTypeInfo, IOwnedItemInfo } from '../../../../ts/models/resourceInfoParts';
import { ContentDraftStatus } from '../../../../ts/ServerApi/types/ContentDraft';
import { RefreshModeTypes } from '../../../../ts/ServerApi/types/IRemoteRefreshProperties';
import { SiteRoles } from '../../../../ts/ServerApi/types/ISiteRole';
import { IServerUserInfo } from '../../../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../../../userManagement/users/models/ISiteUserInfo';
import { IWorkbookInfo } from '../../../../workbook/models/IWorkbookInfo';
import { IContentInfo } from '../../../models/IContentInfo';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../../selectors/SpaceInfoSelectors';
import { isWorkbookSpace, SpaceInfo } from '../../../state/SpaceInfo';


export function isSingleEmbeddedDatasource(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && DataSources.isEmbedded(getCommonDatasourceInfo(targetItems[0]));
}

export function isAnyItemFlowSubscription(subscriptions: ISubscriptionInfo[]): boolean {
  return subscriptions.length >= 1 &&
    _.any(subscriptions, (subscription) => {
      return subscription.targetType === 'flow';
    });
}

export function isSingleUnpublishedFlowDraft(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'flow' && targetItems[0].draftStatus === ContentDraftStatus.unpublished;
}

export function isSingleNonEmbeddedDatasource(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && !DataSources.isEmbedded(getCommonDatasourceInfo(targetItems[0]));
}

export function anyItemIsEmbeddedDatasource(targetItems: IContentInfo[]): boolean {
  return targetItems.length >= 1 &&
    _.any(targetItems, (item => item.contentType === 'datasource'
      && DataSources.isEmbedded(getCommonDatasourceInfo(item))));
}

export function isSingleDatasourceWithExtract(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && !!DataSources.isExtract(getCommonDatasourceInfo(targetItems[0]));
}

export function isSingleDatasourceWithEncyptedExtract(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && !!DataSources.isExtract(getCommonDatasourceInfo(targetItems[0]))
  && !!DataSources.isEncrypted(getCommonDatasourceInfo(targetItems[0]));
}

export function isSingleDatasourceWithUnencyptedExtract(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && !!DataSources.isExtract(getCommonDatasourceInfo(targetItems[0]))
  && !!DataSources.isUnencrypted(getCommonDatasourceInfo(targetItems[0]));
}

export function hasFileBasedDatasource(targetItems: IDataSourceInfo[] | IDataSourceDetailInfo[]): boolean {
  return _.any(targetItems, (datasource: IDataSourceInfo | IDataSourceDetailInfo) =>  datasource.isFileBased);
}

export function hasExtractOnlyDatasource(targetItems: IDataSourceInfo[] | IDataSourceDetailInfo[]): boolean {
  return _.any(targetItems, (ds: IDataSourceInfo | IDataSourceDetailInfo) => ds.isExtractOnly);
}

export function isSingleLiveDatasource(targetItems: IContentInfo[]): boolean {
  return targetItems.length === 1 && targetItems[0].contentType === 'datasource' && !DataSources.isExtract(getCommonDatasourceInfo(targetItems[0]));
}

export function userCanManageAlerts<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(appState: AppState, targetItems: T[]): boolean {
  return targetItems.length > 0 && targetItems.every((item) => {
    return item.contentType === 'dataalert' && CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(item.owner)(appState);
  });
}

export function userIsSubscribedToAnyAlerts(targetItems: IDataAlertInfo[]): boolean {
  return targetItems.some((item) => {
    return item.isCurrentUserSubscribed && item.recipientsCount > 0;
  });
}

export function userIsSubscribedToAllAlerts(targetItems: IDataAlertInfo[]): boolean {
  return targetItems.length > 0 && targetItems.every((item) => {
    return item.isCurrentUserSubscribed && item.recipientsCount > 0;
  });
}

export function isAlertSuspended(targetItems: IDataAlertInfo[]): boolean {
  if(targetItems.length !== 1) {
    return false;
  }
  return (targetItems[0].suspendState !== 'NotSuspended');
}

export function isEditOrResumeEnabled(appState: AppState, targetItems: IDataAlertInfo[]): boolean {
  return !(targetItems.length !== 1 || !userCanManageAlerts(appState, targetItems) || SessionSiteSelectors.siteRole(appState) === SiteRoles.Viewer);
}

export function userCanManageAnyCollection<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(appState: AppState, targetItems: T[]): boolean {
  return targetItems.length > 0 && targetItems.some((item) => {
    return item.contentType === 'collection' && CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(item.owner)(appState);
  });
}

export function isSingleUnShareableCollection(appState: AppState, targetItems: IContentInfo[]): boolean {
  return (targetItems.length === 1 && targetItems[0].contentType === 'collection') &&
    (!FeatureFlags.shareCollectionsEnabled || (!FeatureFlags.collectionsPermissionsEnabled && !SessionSiteSelectors.isPublicCollectionsEnabled(appState)));
}

export function allCollection<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(targetItems: T[]): boolean {
  return targetItems.length > 0 && targetItems.every((item) => {
    return item.contentType === 'collection';
  });
}

export function allRecommendedView<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(targetItems: T[]): boolean {
  return targetItems.length > 0 && targetItems.every((item) => {
    return item.contentType === ExplorableItemTypeValue.recommendedView;
  });
}

// has licence and encryption enable
export function isEncryptionEnabled(appState: AppState): boolean {
  return  !!SessionServerSelectors.extractEncryptionEnabledOmniSwitch(appState) &&
  SessionSiteSelectors.extractEncryptionMode(appState) === ExtractEncryptionMode.ENABLED;
}

// has licence and encryption enable
export function isEncryptionEnforced(appState: AppState): boolean {
  return  !!SessionServerSelectors.extractEncryptionEnabledOmniSwitch(appState) &&
  SessionSiteSelectors.extractEncryptionMode(appState) === ExtractEncryptionMode.ENFORCED;
}

export function isAnyDatasoursEncrypted(datasouces: IDataSourceInfo[]): boolean {
  return _.any(datasouces, ds=> !!DataSources.isExtract(ds) && !!DataSources.isEncrypted(ds));
}

export function isAnyDatasoursUnencrypted(datasouces: IDataSourceInfo[]): boolean {
  return _.any(datasouces, ds=> !!DataSources.isExtract(ds) && !!DataSources.isUnencrypted(ds));
}

export function isDatasoursAllLive(datasouces: IDataSourceInfo[]): boolean {
  return _.all(datasouces, ds=> !DataSources.isExtract(ds));
}

export function isAnyLiveNonFileBasedNorSqlProxyDatasource(targetItems: IDataSourceInfo[]): boolean {
  return _.any(targetItems, ds =>!DataSources.isExtract(ds) && !(ds.isFileBased || ds.connectionType === 'sqlproxy'));
}

export function isAllExtractDatasource(targetItems: IDataSourceInfo[]): boolean {
  return _.all(targetItems, ds =>!!DataSources.isExtract(ds));
}

export function isAnyPendingExtractStatus(datasources: IDataSourceInfo[] | IDataSourceDetailInfo[]): boolean {
  return _.any(datasources, (ds: IDataSourceInfo | IDataSourceDetailInfo) => DataSources.isPendingCreateExtract(ds) ||
  DataSources.isPendingDecryption(ds) ||  DataSources.isPendingEncryption(ds));
}

export function isAnyNonFileBasedDatasources(datasources: IDataSourceInfo[]): boolean {
  return _.any(datasources, ds => !ds.isFileBased);
}

export function isAllDatasoursEncrypted(datasouces: IDataSourceInfo[]): boolean {
  return _.all(datasouces, ds=> !!DataSources.isExtract(ds) && !!DataSources.isEncrypted(ds));
}

export function isWorkbookEncryptedExtract(workbook: IWorkbookInfo): boolean {
  return workbook && (workbook.hasExtracts || workbook.hasEncryptableExtracts) && workbook.extractEncryptionState === ExtractEncryptionState.encrypted;
}

export function isWorkbookUnencryptedExtract(workbook: IWorkbookInfo): boolean {
  return workbook && (workbook.hasExtracts || workbook.hasEncryptableExtracts) && workbook.extractEncryptionState === ExtractEncryptionState.unencrypted;
}

export function canChangeSiteAvailability(targetItems: ISiteSettingsForServerAdmin[], toAvailabilityStateName: string): boolean {
  if (targetItems.length === 1) {
    // Check if default site or if the action will not change availability
    const item = targetItems[0];
    return item.settings.urlName !== ''
      && item.availability !== toAvailabilityStateName
      && item.availability !== SiteAvailabilityState.LOCKED;
  } else {
    return targetItems.length > 1;
  }
}

export function userCanEditSchedule<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(appState: AppState, targetItems: T[]): boolean {
  return _.every(targetItems, (item) => item.contentType === 'schedule') &&
    SessionUserSelectors.isSessionServerAdmin(appState);
}

export function userCanRunSchedule(appState: AppState, targetItems: IScheduleInfo[]): boolean {
  return _.every(targetItems, (item) => item.contentType === 'schedule') &&
    CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(appState);
}

export function userCanEnableLinkedTask(targetItems: IScheduleInfo[]): boolean {
  // Future TODO: add check for Linked Task Site Setting, will need appState
  return _.every(targetItems, (item) => (item.scheduledAction === 'Flow') && (item.parallel));
}

export function anyLinkedTaskEnabledSchedule(targetItems: IScheduleInfo[]): boolean {
  return _.any(targetItems, (item) => (item.enabledForLinkedTasks));
}

export function anyLinkedTaskDisabledSchedule(targetItems: IScheduleInfo[]): boolean {
  return _.any(targetItems, (item) => (item.enabledForLinkedTasks === false));
}

export function anyDisabledSchedule(targetItems: IScheduleInfo[]): boolean {
  return _.any(targetItems, (item) => !isScheduleEnabled(item));
}

export function anyEnabledSchedule(targetItems: IScheduleInfo[]): boolean {
  return _.any(targetItems, (item) => isScheduleEnabled(item));
}

function isScheduleEnabled(schedule: IScheduleInfo): boolean {
  return !!schedule.runNextAt;
}

// TODO delete after TFS1378043 resolved
export function userCanDeleteRunFlowTask<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(targetItems: T[]): boolean {
  return _.every(targetItems, (item) => item.contentType === 'runFlowTask') &&
    targetItems.length > 0;
}

export function userCanEditGroup<T extends IOwnedItemInfo & IExplorableItemTypeInfo>(appState: AppState, targetItems: IGroupInfo[]): boolean {
  return _.every(targetItems, (item) => item.contentType === 'group') &&
  !targetItems[0].isAllUsersGroup && !this.isActiveDirectoryGroup(targetItems[0]);
}

export function isActiveDirectoryGroup(group: IGroupInfo): boolean {
  return (group.domainName && group.domainName !== 'local');
}

export function isSingleSelectSiteUserGuest(users: ISiteUserInfo[]): boolean {
  return (users.length === 1 && users[0].siteRole === SiteRoles.Guest);
}

export function isSingleSelectServerUserGuest(users: IServerUserInfo[]): boolean {
  return (users.length === 1 && users[0].maxSiteRole === SiteRoles.Guest);
}

export function areAllSelectedUsersServerAdmins(users: ISiteUserInfo[]): boolean {
  return _.every(users, (user: ISiteUserInfo) => {
    // return true if every user is server admin
    return (user.siteRole === SiteRoles.ServerAdministrator);
  });
}

export function isSelectingSomethingOtherThanGuest(items: ISiteUserInfo[]): boolean {
  return items.length > 0 && !isSingleSelectSiteUserGuest(items);
}

// has licence and encryption enable
export function extractMoveToTheWebEnabled(isWebExtractionEnabled: boolean, datasources: IDataSourceDetailInfo[]): boolean {
  return isWebExtractionEnabled && isLegalForExtractMoveToTheWeb(datasources);
}

export function isLegalForExtractMoveToTheWeb(datasources: IDataSourceDetailInfo[]): boolean {
  return _.all(datasources, (ds) => ((ds.hasExtracts && ds.refreshMode === RefreshModeTypes.Server) || (!ds.hasExtracts && !ds.usingRemoteQueryAgent)) && !ds.isExtractOnly && !ds.isFileBased);
}

export function userCanCreateExtractOnEmbeddedDs(state: AppState): boolean {
  const ownedItem: IOwnedItemInfo = selectSpaceInfoItem(state) as IWorkbookInfo;
  const isAdminOrOwner = CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownedItem ? ownedItem.owner : null)(state);
  return isAdminOrOwner;
}

export function isVisibleOnEncryptionAction(datasources: IDataSourceDetailInfo[] | IWorkbookInfo[] | FlowBaseInfo[], state: AppState): boolean{
  // for Bridge-based, Published DS File-based Published DS, Extract-only Published DS
  // we will show existing ear button (not for workbook and embedded DS)
  const isWebExtractionEnabled = CompositeSessionInfoSelectors.isWebExtractionEnabled(state);
  const spaceInfo: SpaceInfo = selectSpaceInfo(state);
  const encryptionEnabled = SessionServerSelectors.extractEncryptionEnabledOmniSwitch(state) &&
  SessionSiteSelectors.isSiteEARModeEnabled(state) && !isSingleEmbeddedDatasource(datasources);
  const isFlow = state.availableActionsForEntities && state.availableActionsForEntities.entity.contentType === 'flow';
  // case 1) mttw enabled and on workbook space, no ear button
  // case 2) mttw enabled and on none flow space and it's NOT legal forExtractMoveToTheWeb, no ear button
  // case 3) other case will use existing ear button condition
  if (isWebExtractionEnabled &&
    (isWorkbookSpace(spaceInfo) ||
    (!isFlow && isLegalForExtractMoveToTheWeb(datasources as IDataSourceDetailInfo[])))) {
      return false;
  }
  return encryptionEnabled;
}

export function containsAccelerationSchedule(targetItems: IScheduleInfo[]): boolean {
  return _.any(targetItems, (item) => item.contentType === 'schedule' && item.scheduledAction === ScheduleActions.Data_Acceleration);
}

/**
 * Returns true if one of the selected targetItems in explorer is not accessible to the user (Permission denied)
 * Applicable in Shared with me space
 * @param targetItems Selected items in a space
 */
export function isUserAccessDeniedForTargetItem(targetItems: IExplorableItemTypeInfo[]): boolean {
  for (let i = 0; i < targetItems.length; i++) {
    if ((targetItems[i] as ISharedWithMeContentInfo).isAccessDenied) {
      return true;
    }
  }
  return false;

}

export function workbookTypeOnly(targetItems: IWorkbookInfo[]): boolean {
  return targetItems.every((item) => item.contentType === 'workbook');
}

export function isUsingBridge(datasource: IDataSourceDetailInfo): boolean {
  return datasource.usingRemoteQueryAgent || (datasource.hasExtracts && datasource.refreshMode === RefreshModeTypes.Remote);
}
